<template>
  <Modal
    v-model:visible="isVisible"
    class="find-unplanned-ship-orders-modal"
    size="lg"
    :header="
      t('common.findMoreParam', { param: t('shipOrders.shipOrders', 2) })
    "
    maximizable
    @keypress.enter="onSubmit"
    @keypress.esc="onCancel"
  >
    <template #default>
      <div class="content-heading-container flex flex-gap-1 align-items-center">
        <SavedSearchControls
          v-model:selected-search="selectedSearch"
          :edited="isEdited"
          :popular-searches="popularSearches"
          :saved-searches="savedSearches"
          :header-text="t('orders.orders', 2)"
          header-icon="file-alt"
          readonly
          @clear="onSavedSearchClear"
          @discard="onSavedSearchDiscard"
          @select="onSavedSearchSelect"
        />
        <LoadingIndicator :loading="results.loading" />
      </div>

      <RouteBuilderOrderSearchBuilder
        id="route-builder-order-search-builder"
        :model-value="criteria"
        @update:model-value="onCriteriaChange"
      />

      <SkeletonDataTable
        v-model:selection="selection"
        v-model:expanded-rows="expandedRows"
        class="elevated"
        table-class="p-datatable-sm"
        :value="searchResults"
        :loading="results.loading"
        :number-of-rows="5"
        :first="results.data?.pageable?.offset ?? 0"
        :rows="criteria.pageSize"
        :total-records="results.data?.totalElements"
        :sort-field="criteria.sortField"
        :sort-order="criteria.sortOrder"
        :rows-per-page-options="[10, 20, 50]"
        paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        data-key="id"
        :collapsed-row-icon="PrimeIcons.CHEVRON_DOWN"
        :expanded-row-icon="PrimeIcons.CHEVRON_UP"
        responsive-layout="scroll"
        selection-mode="multiple"
        current-page-report-template="Showing {first} to {last} of {totalRecords}"
        auto-layout
        contextmenu
        lazy
        paginator
        removable-sort
        @page="onPage"
        @sort="onSort"
      >
        <template #empty>
          {{
            t('common.noParamMatchingSearch', {
              param: t('shipOrders.shipOrders', 2),
            })
          }}
        </template>

        <Column selection-mode="multiple" header-style="width: 3em" />

        <Column field="id" :header="t('common.id')" />

        <Column field="origin" :header="t('orders.origin')">
          <template #body="{ data }: DataTableRow">
            <ShippingPointSearchResult :shipping-point="getOrigin(data)" />
          </template>
        </Column>

        <Column field="destination" :header="t('orders.destination')">
          <template #body="{ data }: DataTableRow">
            <ShippingPointSearchResult :shipping-point="getDestination(data)" />
          </template>
        </Column>

        <Column :header="t('lineItems.lineItems', 2)">
          <template #body="{ data }: DataTableRow">
            <RawLineItemSummarySearchResult
              :total-weight="getWeight(data)"
              :total-handling-units="getHandlingUnitCount(data)"
              :handling-unit-label="getHandlingUnitLabel(data)"
            />
          </template>
        </Column>

        <Column :header="t('tags.tags', 2)">
          <template #body="{ data }: DataTableRow">
            <TagContainer
              class="h-auto"
              :model-value="data.tags ?? []"
              readonly
            />
          </template>
        </Column>
      </SkeletonDataTable>
    </template>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        mode="primary"
        :label="
          t('common.addToParam', { param: t('routeBuilder.planningPool') })
        "
        :disabled="!selection.length"
        @click="onSubmit"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import { useLocalStorage } from '@vueuse/core';
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import type { PropType, Ref } from 'vue';
import { ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useExpandedRows,
  usePartitionedStorageKey,
  useToast,
} from '@rygen/rygen-ui/composables';
import { Loadable, Optional, SearchResult } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';
import { Order as EsOrder } from '@rygen/tms-search-model/search/order';

import api from '@/api';
import { useSavedSearches } from '@/composables';
import {
  convertEsOrderShippingPoint,
  Order,
  OrderSavedSearch,
  OrderSearchCriteria,
  OrderSearchData,
  ShipmentStopType,
} from '@/model';
import { AddToPlanningPoolPayload, ValidShipOrder } from '@/model/routeBuilder';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RouteBuilderOrderSearchBuilder from '@/components/routeBuilder/planningPool/search/RouteBuilderOrderSearchBuilder.vue';
import RawLineItemSummarySearchResult from '@/components/search/RawLineItemSummarySearchResult.vue';
import ShippingPointSearchResult from '@/components/search/ShippingPointSearchResult.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import SavedSearchControls from '@rygen/rygen-ui/components/search/SavedSearchControls.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';

type OrderResult = OrderSearchData | EsOrder;

export type DataTableRow = {
  data: OrderResult;
  index: number;
};

const buildNewCriteria = (criteria?: OrderSearchCriteria) => {
  const existingCriteria = criteria ?? {};
  return new OrderSearchCriteria({
    ...existingCriteria,
    excludedIds: planningPoolShipOrders.value
      .map((so) => so.orderId)
      .filter(notNull),
    ownerNaturalIds: owningClient.value?.naturalId
      ? [owningClient.value.naturalId]
      : undefined,
    hasAnyUnplannedShipOrders: true,
  });
};

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  planningPoolShipOrders: {
    type: Array as PropType<ValidShipOrder[]>,
    default: () => [],
  },
});
const { planningPoolShipOrders } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'submit', payload: AddToPlanningPoolPayload): void;
}>();

const isElasticSearchEnabled = useFlag('elasticsearch-tms-feat-flag');

const { currentUser, selectedDomain } = useCurrentUserStore();
const { owningClient } = useParentEntityStore();
const { storageKey: useNewSearchKey } = usePartitionedStorageKey(
  'use-new-order-search',
  {
    currentUser,
    selectedDomain,
  },
);
const useNewSearch = useLocalStorage(
  useNewSearchKey.value,
  isElasticSearchEnabled.value,
);

const results: Ref<Loadable<SearchResult<OrderResult>>> = ref({
  loading: false,
});
const searchResults = toRef(() => results.value.data?.content ?? []);

const { expandedRows } = useExpandedRows(searchResults);
const { t } = useI18n();
const {
  loadPopularSearches,
  loadSavedSearches,
  popularSearches,
  savedSearches,
} = useSavedSearches('Order');
const toast = useToast();

const cancelRequest: Ref<Optional<CancelTokenSource>> = ref();
const criteria: Ref<OrderSearchCriteria> = ref(buildNewCriteria());
const isEdited = ref(false);
const selectedSearch: Ref<Optional<OrderSavedSearch>> = ref();
const selection: Ref<OrderResult[]> = ref([]);

const getOrigin = (result: OrderResult) => {
  return result instanceof OrderSearchData
    ? result.origin
    : convertEsOrderShippingPoint(result.origin, ShipmentStopType.PICKUP);
};

const getDestination = (result: OrderResult) => {
  return result instanceof OrderSearchData
    ? result.destination
    : convertEsOrderShippingPoint(
        result.destination,
        ShipmentStopType.DROP_OFF,
      );
};

const getWeight = (result: OrderResult) => {
  return result instanceof OrderSearchData
    ? result.weight
    : result.lineItemSummary?.totalWeight?.value;
};

const getHandlingUnitCount = (result: OrderResult) => {
  return result instanceof OrderSearchData
    ? result.handlingUnitCount
    : result.lineItemSummary?.totalHandlingQuantity;
};

const getHandlingUnitLabel = (result: OrderResult) => {
  return result instanceof OrderSearchData
    ? result.handlingUnitDescription
    : result.lineItemSummary?.handlingUnit?.name;
};

const onSavedSearchClear = () => {
  criteria.value = buildNewCriteria();
  isEdited.value = false;
};

const onSavedSearchSelect = (savedSearch: OrderSavedSearch) => {
  criteria.value = buildNewCriteria(savedSearch?.searchData);
  criteria.value.savedSearchId = savedSearch.id;
  isEdited.value = false;
};

const onSavedSearchDiscard = (savedSearch?: OrderSavedSearch) => {
  criteria.value = buildNewCriteria(savedSearch?.searchData);
  isEdited.value = false;
};

const onCriteriaChange = (newVal: OrderSearchCriteria) => {
  criteria.value = buildNewCriteria(newVal);
  criteria.value.page = 0;
  isEdited.value = true;
};

const onPage = (event: DataTablePageEvent) => {
  selection.value = [];
  criteria.value.pageSize = event.rows as number;
  criteria.value.page = event.page as number;
};

const onSort = (event: DataTableSortEvent) => {
  criteria.value.sortField = event.sortField as keyof Order | undefined;
  criteria.value.sortOrder = (event.sortOrder ?? 0) as -1 | 0 | 1;
};

const onSubmit = async () => {
  isVisible.value = false;
  const orderIds = selection.value.map((o) => o.id).filter(notNull);
  emit('submit', { orderIds });
};

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};

const runSearch = async () => {
  if (cancelRequest.value) {
    cancelRequest.value.cancel();
  }

  cancelRequest.value = axios.CancelToken.source();

  try {
    const rawCriteria = criteria.value;
    const cancelToken = cancelRequest.value;
    const useElasticSearch = isElasticSearchEnabled.value && useNewSearch.value;
    results.value.loading = true;
    results.value.data = useElasticSearch
      ? await api.order.searchWithElasticSearch(rawCriteria, {
          cancelToken: cancelToken,
          useCache: false,
          useSelectedDomainContext: false,
        })
      : await api.order.search(rawCriteria, {
          cancelToken: cancelToken,
          useCache: false,
          useSelectedDomainContext: false,
        });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    results.value.error = e;
    console.error('Error loading orders', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('orders.orders', 2),
      }),
      error: e,
    });
  } finally {
    results.value.loading = false;
  }
};
const runSearchDebounced = debounce(runSearch, 500);

const initializeSearch = async () => {
  await Promise.all([loadPopularSearches(), loadSavedSearches()]);
  const defaultSearch = savedSearches.value.data?.find((s) => s.defaultSearch);
  selectedSearch.value = defaultSearch;

  if (selectedSearch.value) {
    onSavedSearchSelect(selectedSearch.value);
  }

  runSearchDebounced();
};

watch(
  planningPoolShipOrders,
  (newVal) => {
    criteria.value.excludedIds = newVal.map((so) => so.orderId).filter(notNull);
  },
  { deep: true, immediate: true },
);

watch(
  criteria,
  () => {
    if (isVisible.value) {
      runSearchDebounced();
    }
  },
  { deep: true },
);

watch(
  isVisible,
  (newVal) => {
    if (newVal) {
      criteria.value = buildNewCriteria();
      results.value = { loading: true };
      selection.value = [];
      initializeSearch();
    }
  },
  { immediate: true },
);
</script>

<style>
.find-unplanned-ship-orders-modal.rygen-modal.size-lg {
  --p-datatable-header-cell-selected-background: var(--p-highlight-background);
  /* TODO: set --rygen-modal-max-height in rygen ui and calculate content-max-height based on it minus padding */
  --rygen-modal-content-max-height: 70vh;
  --rygen-modal-content-overflow: hidden auto;
  --rygen-modal-max-width: 85vw;
  --rygen-modal-width: 85vw;

  &.p-dialog-maximized {
    --rygen-modal-max-width: 100vw;
  }

  .modal-content {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);

    .data-table-container {
      max-width: none;

      .p-datatable {
        position: relative;

        .p-datatable-table-container {
          height: 100%;
          /* `!important` to override PrimeVue element style */
          overflow: initial !important;

          .p-datatable-thead {
            position: sticky;
            top: 0;
            z-index: 2;
          }
        }

        .p-datatable-paginator-bottom {
          position: sticky;
          bottom: 0;
        }
      }
    }
  }
}
</style>
